import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';
import BackButton from '../components/BackButton';

const ArtWorldLandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { artwork } = location.state || {};

  // Debug: Log the received artwork
  console.log('ArtWorldLandingPage - Received artwork:', artwork);
  console.log('ArtWorldLandingPage - Location state:', location.state);
  console.log('ArtWorldLandingPage - Artwork type:', typeof artwork);
  console.log('ArtWorldLandingPage - Artwork keys:', artwork ? Object.keys(artwork) : 'No artwork');

  const handleEnterArtWorld = () => {
    console.log('ArtWorldLandingPage - Navigating to selection with artwork:', artwork);
    navigate('/art-world/selection', { 
      state: { 
        artwork: artwork,
        fromPage: 'landing'
      } 
    });
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#FFF8E1]">
      {/* Amber color splashes background - matching home page */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      
      {/* Splash images merged into background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-10 right-5 opacity-20"
          style={{ 
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)'
          }}
        />
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
        style={{
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)'
        }}
      />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      <div className="relative z-10 p-3 sm:p-4 md:p-6">
      {/* Back + Actions */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <BackButton to="/magic" label="Back" />
        <div className="ml-auto flex items-center gap-1 md:gap-3">
          <button title="All activities you do are auto-saved in My Activities" onClick={() => navigate('/activity-dashboard?view=magic-studio', { state: { view: 'magic-studio' } })} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">My Activities</button>
          <button onClick={() => navigate('/artist-wall')} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">My Gallery</button>
          {isAuthenticated && (
            <button onClick={async()=>{ try{ await logout(); }catch(_){}; try{ localStorage.removeItem('mentor_session'); window.dispatchEvent(new Event('mentor_session_changed')); }catch(_){}; navigate('/'); }} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">Sign Out</button>
          )}
        </div>
      </div>

        {/* Hero Section */}
        <div className="text-center mb-6 md:mb-12">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-2 md:mb-4">
            Welcome to Ways of Seeing
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-slate-700 mb-4 md:mb-8 max-w-2xl mx-auto px-4">
            Transform your artwork into different art styles and learn about amazing artists from history!
          </p>
        </div>

        {/* Main Content - Artwork on Left, Styles on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-12">
          {/* Left Panel - Video Placeholder */}
          <div className="original-artwork flex flex-col items-center">
            <div className="artwork-container w-full max-w-[500px]">
              <div className="relative w-full aspect-square bg-amber-100 border-2 md:border-4 border-amber-300 rounded-xl md:rounded-2xl flex items-center justify-center shadow-xl md:shadow-2xl">
                <span className="text-amber-700 text-sm md:text-lg font-semibold px-4 text-center">Video Coming Soon</span>
              </div>
              <div className="text-center text-slate-700 font-semibold mt-2 bg-white/80 px-3 py-1 rounded text-xs md:text-sm">
                (Video coming soon: How to use Art World!)
              </div>
            </div>
          </div>

          {/* Right Panel - Feature Preview */}
          <div className="feature-preview flex flex-col justify-start">
            {/* Golden Description Box - Aligned with video top */}
            <div className="rounded-lg md:rounded-xl p-3 md:p-4 shadow-lg border-2 border-amber-300 mb-4 md:mb-6 bg-white/90 backdrop-blur-sm">
              <h4 className="font-bold text-slate-900 text-base md:text-lg mb-2 text-center">What is Art World?</h4>
              <p className="text-slate-700 text-xs md:text-sm leading-relaxed text-center">
                Transform your artwork into different art styles and learn about famous artists and art movements. 
                Explore creativity through interactive learning experiences and discover the magic of art history!
              </p>
            </div>
            
            {/* Feature Grid - Horizontal Layout */}
            <div className="feature-grid grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
              <div className="feature-thumbnail cursor-pointer rounded-lg md:rounded-xl p-3 md:p-4 border-2 transition-all duration-300 bg-amber-100/80 backdrop-blur-sm hover:scale-105 border-amber-200 hover:border-amber-300 hover:shadow-md">
                <h4 className="font-semibold text-amber-800 text-xs md:text-sm mb-1 md:mb-2 text-center">
                  Transform Artwork
                </h4>
                <p className="text-[10px] md:text-xs text-amber-700 text-center">
                  See your artwork in different art styles like Impressionism, Cubism, and Renaissance!
                </p>
              </div>

              <div className="feature-thumbnail cursor-pointer rounded-lg md:rounded-xl p-3 md:p-4 border-2 transition-all duration-300 bg-amber-100/80 backdrop-blur-sm hover:scale-105 border-amber-200 hover:border-amber-300 hover:shadow-md">
                <h4 className="font-semibold text-amber-800 text-xs md:text-sm mb-1 md:mb-2 text-center">
                  Learn Art History
                </h4>
                <p className="text-[10px] md:text-xs text-amber-700 text-center">
                  Discover famous artists and art movements through interactive content!
                </p>
              </div>

              <div className="feature-thumbnail cursor-pointer rounded-lg md:rounded-xl p-3 md:p-4 border-2 transition-all duration-300 bg-amber-100/80 backdrop-blur-sm hover:scale-105 border-amber-200 hover:border-amber-300 hover:shadow-md">
                <h4 className="font-semibold text-amber-800 text-xs md:text-sm mb-1 md:mb-2 text-center">
                  Interactive Learning
                </h4>
                <p className="text-[10px] md:text-xs text-amber-700 text-center">
                  Watch educational videos and explore art styles with fun activities!
                </p>
              </div>
            </div>
            
            {/* Enter Button - Now under thumbnails */}
            <div className="text-center">
              <button 
                onClick={handleEnterArtWorld}
                className="enter-artworld-btn bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 md:px-8 md:py-4 rounded-lg md:rounded-xl text-sm md:text-xl font-semibold shadow-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl w-full sm:w-auto"
              >
                Let Your Art Enter the World of Art
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtWorldLandingPage; 